import React, { useMemo, useRef, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import _ from 'lodash'
import fetch from '@adc/fetch'
import configment from '@adc/configment'
import instrument from '@adc/instrument'
import analytics from '@adc/analytics'
import {
  hooks,
  ApplicationNotificationsProvider,
  ApplicationThemeProvider,
  ProductLine,
  useApplicationThemeRaw,
} from '@adc/ui-core'
import type { Theme } from '@adc/ui-core'
import type { primitives } from '@adc/blueprint'
import AnnouncementBanner, {
  parseAnnouncement,
  isAnnouncementDismissed,
} from '../announcementBanner'
import ApplicationHeader from './applicationHeader'
import { ApplicationContextProvider } from './context'
import ApplicationErrorBoundary from './errorBoundary'
import { ApplicationModalsProvider, useApplicationModals } from './modals'
import { ApplicationDrawersProvider } from './drawers'
import SharedPageMetadata from './sharedPageMetadata'
import Footer from '../footer'
import { ApplicationQueuesProvider } from './queues'
import { ApplicationSurveyProvider } from './applicationSurvey'
import classNames from 'classnames/bind'
import cls from './shell.scss'
import SurveyButton from '../surveyButton'
import { enums } from '@adc/sdk'
import useFreshnessDetection from '../../hooks/useFreshnessDetection'
import type { Capabilities } from './types'

const cx = classNames.bind(cls)

//
// NOTES:
// - Need to design a mutually exclusive data structure so that the application always knows which modal is open at any
//   point
// - Need to design a `FloatingMessage` notifier that serves the purpose of `resi-search` and other apps use the
//   `Message` component for.
// - We need to solve the Helmet issue not being able to store favicons from inside here because it's stored as a data
//   uri. We may need to add a special case here to our policy of libraries being self-building.
// - Add in capabilities which would define shared behaviors all applications inherit, while providing a simple way for
//  applications to pass more details that the shared details should take into account.
//  ```
// capabilities: {
//   authentication: {},
//   criticalAreaVerification: {},
//   accessibility: {
//     equalweb: {}
//   }
// },
//  ```
//
// TODO:
// - We could track page loads using @adc/analytics in one place here so the apps don't have to deal with that, and we
//   can ensure everything is tracked consistently.
// - Could we use this to inject global styles so the app does not have to remember to import them for the app to render
//   correctly if they use this shell?.
export const DEFAULT_CAPABILITIES = Object.freeze({
  notifications: {},
  accessibility: {
    equalweb: { site_key: '', enabled: false },
  },
  onboarding: {
    enabled: false,
  },
})

const DEFAULT_THEME: Theme = {
  productLine: ProductLine.AuctionCom,
  version: 1,
}

const isShortTermExpiredError = (error: any) =>
  error?.extensions?.code === 'BACKEND_UNAUTHORIZED_ERROR' &&
  error?.extensions?.specificErrorCode ===
    enums.UserShortTermStatus.ShortTermExpired

// Intercept graph calls that fail because the current user's short_term authorization has expired. Handle such
// failures by challenging the user for their password, and reattempt the call if the user successfully refreshes
// their token.
const ShortTermExpiredIntercept = () => {
  // This only works with sufficiently new versions of @adc/fetch.
  if (!('staticOptions' in (fetch as any))) {
    return null
  }

  const modals = useApplicationModals()
  ;(fetch as any).staticOptions.parseResponse = async (response: any) => {
    const result = await (fetch as any).responseParsers.json(response)

    const onClose = (deferred: { reject: (reason: Error) => void }) => () => {
      deferred.reject(new Error('User verification failed'))
    }
    const onSuccess = (deferred: { resolve: () => void }) => () => {
      deferred.resolve()
    }

    if ((result?.body?.errors || []).some(isShortTermExpiredError)) {
      if ((response as any).timeoutID) {
        clearTimeout((response as any).timeoutID)
      }

      const deferred = Promise.withResolvers()

      modals.open('verification', {
        onClose: onClose(deferred as any),
        onSuccess: onSuccess(deferred as any),
      })

      await deferred.promise

      throw new (fetch as any).RetryError()
    }

    return result
  }

  return null
}

interface ShellClassOverrides {
  container?: string
  header?: string
  content?: string
  footer?: string
}

/**
 * Values derived from the request server-side (typically cookies) that the
 * shell needs at first paint to render SSR-correct markup and avoid hydration
 * mismatches / flashes. Apps populate this explicitly (do not dump raw cookies)
 * and reconstruct the identical object client-side before hydration.
 */
export interface ShellSsrHints {
  /** Dismissal fingerprint for the announcement banner (from its cookie). */
  announcementDismissedFingerprint?: string
}

interface AuctionApplicationShellProps {
  children: ReactNode
  sspConfig?: Record<string, any>
  user?: primitives.AuctionUser
  auth?: Record<string, any>
  headerProps?: Record<string, any>
  footerProps?: Record<string, any>
  helmetConfig?: Record<string, any>
  featureFlags?: Record<string, any>
  overridableComponents?: Record<string, ReactNode>
  capabilities?: Capabilities
  embeddedContext?: string
  cls?: ShellClassOverrides
  theme?: Theme
  viewingFrom?: enums.CurrentTargetAudience
  history?: { listen: (listener: (location: any) => void) => void }
  domainEventSource?: string
  /**
   * Request-derived hints (usually cookies) needed for SSR-correct first paint.
   * Optional: without it, features fall back to client-side reconciliation.
   */
  ssrHints?: ShellSsrHints
}

const AuctionApplicationShell = ({
  children,
  sspConfig = {},
  user = { logged_in: false },
  auth,
  headerProps,
  footerProps,
  helmetConfig = {},
  featureFlags = {},
  overridableComponents = {},
  capabilities = {},
  embeddedContext,
  cls: clsOverrides = {},
  theme: themeInjected,
  viewingFrom = enums.CurrentTargetAudience.Buyer,
  history,
  domainEventSource = analytics.defaultSource(),
  ssrHints,
}: AuctionApplicationShellProps) => {
  useFreshnessDetection(user)

  // This is a shared well-known trigger for our integration tests to wait before testing. And also adds an attribute to
  // indicate which app is currently serving the page. Which may seem trivial, but apart from allowing simple assertions
  // that we are on the correct app, it also helps in cases where we are restructuring and extracting things from one
  // app to another, and are rolling out the traffic between the two.
  hooks.useMountEffect(() => {
    // @ts-ignore next-line
    instrument.initialize()
    document.body.setAttribute('data-app-state', 'ready')
    document.body.setAttribute('data-app-id', sspConfig.APP_NAME)
    const { pageTracking } = capabilities
    if (pageTracking?.enabled && pageTracking.options?.domainEvents) {
      const productLine = configment.get('APP_THEME', ProductLine.AuctionCom)
      analytics.trackInstrumentPageLoad(user, productLine, domainEventSource)
      setTimeout(() => {
        // we delay the setting of the listener as to avoid possible duplicate initial page load tracking
        // (this is possible if there are nested routers, such as in Seller flow section)
        history?.listen(_location => {
          // this will listen for the react router url change
          analytics.trackInstrumentPageLoad(
            user,
            productLine,
            domainEventSource
          )
        })
      })
    }
  })

  /**
   * There's a lot going on here to handle different shell usages:
   * - themeInjected: For passing theme in directly via theme prop
   * - themeFromContext: For using theme from context... this may seem weird as we set theme context in here as well,
   *     but this is to accommodate cases where the shell is used in a context where the theme is already set.
   * - themeFromConfigE: For using theme from sspConfig (passed in) or configment directly
   */
  const themeFromContext = useApplicationThemeRaw()
  const theme = useMemo(() => {
    let newTheme = DEFAULT_THEME

    if (themeInjected) {
      newTheme = themeInjected
    } else if (themeFromContext) {
      newTheme = themeFromContext
    } else {
      const themeFromConfig =
        sspConfig?.APP_THEME ||
        configment.get('APP_THEME', ProductLine.AuctionCom)

      const versionFromConfig =
        Number(
          sspConfig?.APP_THEME_VERSION || configment.get('APP_THEME_VERSION', 1)
        ) || 1

      if (themeFromConfig) {
        if (Object.values(ProductLine).includes(themeFromConfig)) {
          newTheme = {
            productLine: themeFromConfig,
            version: versionFromConfig,
          }
        }
      }
    }
    return newTheme
  }, [
    sspConfig?.APP_THEME,
    sspConfig?.APP_THEME_VERSION,
    themeFromContext,
    themeInjected,
  ])

  // used to hand off element where we want to render page banner
  const pageBannerContainerRef = useRef(null)

  const announcementFlagValue = featureFlags['fe.resi.announcement-banner']
  const announcementDismissedFingerprint =
    ssrHints?.announcementDismissedFingerprint

  // Reserve banner space on first paint when the flag is on and the (optional,
  // server-provided) dismissal fingerprint doesn't already dismiss it.
  const [announcementVisible, setAnnouncementVisible] = useState(
    () =>
      Boolean(parseAnnouncement(announcementFlagValue)) &&
      !isAnnouncementDismissed(
        announcementFlagValue,
        announcementDismissedFingerprint
      )
  )
  const onAnnouncementVisibilityChange = useCallback(
    (visible: boolean) => setAnnouncementVisible(visible),
    []
  )

  const productLineFromHost = hooks.useProductLineFromHost()

  const memoizedCapabilities = useMemo(() => {
    /**
     * Get accessibility options from configment per product line e.g smartsale, auctioncom.
     * in format of { equalweb: { site_key: 'site key', enabled: true } }
     */
    const accessibility =
      (configment.get('accessibility', {}) as Record<string, any>)[
        productLineFromHost!
      ] || {}

    return _.merge(
      {},
      DEFAULT_CAPABILITIES,
      {
        accessibility,
        authentication: {
          enabled: false,
          options: {},
        },
        surveyMonkey: {
          enabled: featureFlags['fe.resi.enable_feedback_button'],
        },
      },
      capabilities
    )
  }, [capabilities, featureFlags, productLineFromHost])

  const memoizedPageBannerRef = useMemo(
    () => capabilities.notifications?.pageBannerRef || pageBannerContainerRef,
    [capabilities.notifications, pageBannerContainerRef]
  )
  return (
    <ApplicationContextProvider
      sspConfig={sspConfig}
      user={user}
      auth={auth}
      featureFlags={featureFlags}
      capabilities={memoizedCapabilities}
      embeddedContext={embeddedContext as any}
    >
      <ApplicationThemeProvider theme={theme}>
        <ApplicationErrorBoundary user={user}>
          <React.StrictMode>
            <ApplicationQueuesProvider>
              <ApplicationSurveyProvider>
                <ApplicationNotificationsProvider
                  pageBannerContainerRef={memoizedPageBannerRef}
                >
                  <ApplicationModalsProvider>
                    <ApplicationDrawersProvider
                      onboardingProps={capabilities.onboarding}
                    >
                      <SharedPageMetadata
                        helmetConfig={helmetConfig}
                        sspConfig={sspConfig}
                        featureFlags={featureFlags}
                        accessibilityOptions={
                          memoizedCapabilities.accessibility
                        }
                      />
                      <SurveyButton
                        isCapabilityEnabled={
                          memoizedCapabilities.surveyMonkey?.enabled
                        }
                        viewingFrom={viewingFrom}
                      />
                      <AnnouncementBanner
                        flagValue={announcementFlagValue}
                        elmId='announcement_banner'
                        dismissedFingerprint={announcementDismissedFingerprint}
                        onVisibilityChange={onAnnouncementVisibilityChange}
                      />
                      <div className={cx('container', clsOverrides.container)}>
                        <div className={cx('header', clsOverrides.header)}>
                          {overridableComponents.header ? (
                            React.isValidElement(
                              overridableComponents.header
                            ) ? (
                              React.cloneElement(
                                overridableComponents.header as React.ReactElement,
                                { notificationDismissed: !announcementVisible }
                              )
                            ) : (
                              overridableComponents.header
                            )
                          ) : (
                            <ApplicationHeader
                              // @ts-ignore next-line
                              headerProps={{
                                ...headerProps,
                                notificationDismissed: !announcementVisible,
                              }}
                              featureFlags={featureFlags}
                            />
                          )}
                        </div>
                        <div ref={pageBannerContainerRef} />
                        <div
                          className={cx('main-content', clsOverrides.content)}
                        >
                          {children}
                        </div>
                        <div className={cx('footer', clsOverrides.footer)}>
                          {overridableComponents.footer ?? (
                            <Footer {...footerProps} />
                          )}
                        </div>
                      </div>
                      <ShortTermExpiredIntercept />
                    </ApplicationDrawersProvider>
                  </ApplicationModalsProvider>
                </ApplicationNotificationsProvider>
              </ApplicationSurveyProvider>
            </ApplicationQueuesProvider>
          </React.StrictMode>
        </ApplicationErrorBoundary>
      </ApplicationThemeProvider>
    </ApplicationContextProvider>
  )
}

export default AuctionApplicationShell
